import styled, { css, keyframes } from 'styled-components';
import { Form as unformForm } from '@unform/web';

const bgDark = '#121212';
const cardDark = '#1E1E1E';
const textLight = '#EAEAEA';
const textSecondary = '#A0A0A0';
const border = 'rgba(255,255,255,0.1)';
const green = '#1DB954';
const amber = '#FFC107';
const red = '#dc3545';
const primary = '#256af4';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${bgDark};
  color: ${textLight};
  align-items: center;
  width: 100%;
  overflow-x: hidden;
  * {
    box-sizing: border-box;
  }

  /* react-responsive-modal overrides */
  .v2Modal {
    background: ${cardDark};
    border: 1px solid ${border};
    border-radius: 12px;
    padding: 32px 24px;
    color: ${textLight};
    max-width: 560px;
    width: 90vw;
  }
`;

export const Form = styled(unformForm)`
  width: 100%;
  max-width: 1080px;
  margin: 120px auto 0 auto;
  display: flex;
  position: relative;

  div {
    input:not([type='checkbox']) {
      background: ${cardDark};
      color: ${textLight};
      border: 1px solid ${border};
      border-radius: 8px;
    }
  }
`;

const appear = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Content = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 24px auto 40px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
`;

export const PrimaryButton = styled.button<{ variant?: 'primary' | 'green' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.variant === 'primary' ? primary : green)};
  color: ${(p) => (p.variant === 'primary' ? '#fff' : '#000')};
  border: 0;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const Table = styled.table`
  width: 100%;
  border-spacing: 0 12px;
  animation: ${appear} 0.3s ease both;

  thead th {
    background: ${bgDark};
    color: ${textSecondary};
    font-weight: 600;
    text-align: left;
    padding: 6px 10px;
  }

  tbody tr {
    background: ${cardDark};
    border: 1px solid ${border};
    border-radius: 12px;
    overflow: hidden;
  }
  tbody td {
    padding: 10px;
    vertical-align: middle;
  }

  tbody td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
  tbody td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  /* Mobile card layout */
  @media (max-width: 640px) {
    thead {
      display: none;
    }
    tbody tr {
      display: block;
      margin: 0 0 12px 0;
      border-radius: 12px;
      overflow: hidden;
    }
    tbody td,
    tbody td:first-child,
    tbody td:last-child {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-radius: 0;
      padding: 10px 12px;
      border-bottom: 1px solid ${border};
      width: 100%;
      box-sizing: border-box;
    }
    tbody td:last-child {
      border-bottom: 0;
    }
    tbody td::before {
      content: attr(data-label);
      color: ${textSecondary};
      margin-right: 12px;
      flex: 0 0 auto;
    }
    /* Ensure accounts table cells occupy full width on mobile */
    &.accountsTable td,
    &.accountsTable td:first-child,
    &.accountsTable td:last-child {
      width: 100% !important;
      white-space: normal;
      overflow: visible;
      text-overflow: initial;
    }
  }

  /* Specific layout adjustments for the Accounts table */
  &.accountsTable {
    table-layout: fixed;
  }
  &.accountsTable th,
  &.accountsTable td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &.accountsTable th:nth-child(1),
  &.accountsTable td:nth-child(1) {
    width: 36%;
  }
  &.accountsTable th:nth-child(2),
  &.accountsTable td:nth-child(2) {
    width: 22%;
  }
  &.accountsTable th:nth-child(3),
  &.accountsTable td:nth-child(3) {
    width: 22%;
  }
  &.accountsTable th:nth-child(4),
  &.accountsTable td:nth-child(4) {
    width: 20%;
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

export const IconButton = styled.button<{ tone?: 'normal' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: transparent;
  color: ${textLight};
  border: 1px solid ${border};
  border-radius: 10px;
  ${(p) =>
    p.tone === 'danger' &&
    css`
      color: ${red};
      border-color: ${red};
    `}
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const SectionHeader = styled.div`
  margin-top: 24px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
`;

export const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 12px;
`;

export const ModalRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
`;
